import React from "react";
import styled, { css } from "styled-components";
import {
  asType,
  boundaries,
  CONSTANTS,
  escapeChars,
  formatText,
  parseCSS,
  parseNumber,
} from "scripts/FunctionsBundle";
import { Link } from "react-router-dom";

// ################################ INTERFACES & PROPS ################################

type _Base = import("utils/class-types").Base;
type _CSSProperties = import("react").CSSProperties;
type _HTMLProps = import("react").AllHTMLAttributes<HTMLAllCollection>;
type _AviableElements = import("react").ReactHTML;
// DetailedHTMLProps<React.HTMLAttributes<HTMLAnchorElement & HTMLLabelElement>, HTMLAnchorElement & HTMLLabelElement >

type TextProps = {
  /** Text to proccess and show */
  _text: string;

  /** Paragraph's `textAlign`. `default: "left"` */
  _align?: "center" | "right" | "left";
  /** Paragraph's main style. `default: "body"` */
  _type?: "title" | "subtitle" | "button" | "link" | "body";
  /** Paragraph's sub-style from the given `_type`. `default: 1` */
  _variant?: 1 | 2 | 3;

  _colors?: {
    _text?: string;
    _emphasis?: string;
    _hover?: string;
    _active?: string;
    _visited?: string;
    _back?: string;
    _border?: string;
  };

  /** If true, removes the Paragraph margins. Default: `false` */
  _del_margin?: boolean;

  /** `styleText`'s props */
  _styleText?: {
    /** Aspecto **BOLD** `default: "*"` */
    _bold?: {
      /**Alias para representar este estilo */
      _wrap?: string; // "*";
    };
    /** Aspecto *ITALICS* `default: "_"` */
    _italics?: {
      /**Alias para representar este estilo */
      _wrap?: string; // "_";
    };
    /** Aspecto ~~CROSS~~ `default: "~"` */
    _cross?: {
      /**Alias para representar este estilo */
      _wrap?: string; // "~";
    };
    /** Transparente al 50% `default: "#"` */
    _mute?: {
      /**Alias para representar este estilo */
      _wrap?: string; // "#";
    };
    /** Subrayado `default: "&"` */
    _underline?: {
      /**Alias para representar este estilo */
      _wrap?: string; // "&";
    };
    /** Color personalizado `default: "$"` */
    _emphasis?: {
      /**Alias para representar este estilo */
      _wrap?: string; // "$";
      /**Colores para `emphasis`, `default: ["#1757cd"]`*/
      _col?: Array<string>;
    };
    /** LINK (< a/>) `default: "="` */
    _link?: {
      /**Alias para representar este estilo */
      _wrap?: string; // "=";
      /**URLs para los links, `default: ["/"]`*/
      _to?: Array<string>;
      /**Estilos para los links, `default: ["1"]`*/
      _as?: Array<string>;
    };
    /** Tamaño de letra personalizado `default: "^"` */
    _fontSize?: {
      /**Alias para representar este estilo */
      _wrap?: string; // "^";
      /**Tamaños de letra personalizados, `default: ["16px"]`*/
      _size?: Array<string>;
    };
    /** Estilo personalizado `default: "@"` */
    _custom?: {
      /**Alias para representar este estilo */
      _wrap?: string; // "@";
      /**Estilos personalizados y/o nombres de clases personalizadas, `default: [{}]`*/
      _add?: Array<{ _style?: _CSSProperties; _className?: string }>;
    };
    /** Convierte el elemento en otro especificado `default: "?"` */
    _converter?: {
      /** Alias para representar este estilo */
      _wrap?: string; // "?";
      /** Elementos HTML para interpretar `default: ["span"]` */
      _el?: Array<keyof _AviableElements>;
      /** Props de HTML para el elemento `default: [{}]` */
      _prop?: Array<_HTMLProps>;
    };

    /** `props` de integración con `formatText` para aplicar formato automáticamente */
    _formatText?: {
      /**Permitir el uso de `formatText`, `default: true`*/
      _enable?: boolean;
      /**Saltos de línea manuales `default: ";;"`*/
      _newLine?: string;
      /**Traducir el texto con i18n **(DESACTIVADO)** `default: true`*/
      _translate?: boolean;
    };

    /** Control char to match at the start of any style command, `default: "|"` */
    _controlChar?: string;
  };

  /** Add global styles to the Paragraph element just as it would be in a child element.
   * This props serves as a cheat sheet for `in-string` (IS) commands. **NOTE**: any alias
   * shown will be its default value, e.g.: if you set the `bold wrap` to be `"bold"`, then the
   * IS alias will be `"bold"`. To enable a style simply create it (`{}` works).
   */
  _this?: {
    /** Default IS alias: `"*"` */
    _bold?: {};
    /** Default IS alias: `"_"` */
    _italics?: {};
    /** Default IS alias: `"~"` */
    _cross?: {};
    /** Default IS alias: `"#"` */
    _mute?: {};
    /** Default IS alias: `"&"` */
    _underline?: {};
    /** Default IS alias: `"$"` */
    _emphasis?: {
      /** Default IS alias: `"col"`, `default: "#1757cd"` */
      _col?: string;
    };
    /** Default IS alias: `"="` */
    _link?: {
      /** Default IS alias: `"to"`, `default: "/"`*/
      _to?: string;
      /** Default IS alias: `"as"`, `default: "1"`*/
      _as?: string;
    };
    /** Default IS alias: `"^"` */
    _fontSize?: {
      /** Default IS alias: `"size"`, `default: "16px"` */
      _size?: string;
    };
    /** Default IS alias: `"@"` */
    _custom?: {
      /** **NOTE**: this property only accepts `className`s within IS,
       * Default IS alias: `"add"`, `default: ""` */
      _add?: string | _CSSProperties;
    };
    /** Default IS alias: `"?"` */
    _converter?: {
      /** Default IS alias: `"el"`, `default: "span"` */
      _el?: keyof _AviableElements;
      /** **NOTE**: this property only accepts `array` indexes within IS,
       * Default IS alias: `"prop"`, `default: "$0"` */
      _prop?: _HTMLProps;
    };
  };

  /** Paragraph's `LegacyRef` */
  _ref?: React.LegacyRef<any>;
} & _Base;

// ############################ STYLE TEXT SEARCHER ############################
/**
 * Provides a `RegExp` to interpretate text commands within the `styleTextv2` function.
 * Searches for a control character and returns every tag and every parameter next to it.
 * Considerating the default control character `|`:
 * A valid selection starts with a pattern like `|{X:prop1,Y:prop2}|` where `X` and `Y` represents a valid prop
 * to apply to the text contained. Every property must be separated with a `,` and opcionally followed by a `:` to
 * pass some values. Alternatively you can pass a `$1` value to indicate an index in a correspondent `Array` of
 * properties.
 * The selection ends with a pattern like `||`.
 * @param controlChar Control character to start a valid match. `default: "|"`
 * @returns The `RegExp` ready to use
 */
export function styleTextSearch(controlChar = "|"): RegExp {
  if (controlChar === "") controlChar = " ";
  // Escape any REGEXP special character for integrity
  controlChar = controlChar.replace(CONSTANTS._REGEXPS._escapeRegExp, "\\$&");

  // Create the REGEXP - With control character
  // VERSION 7 - \|(?!\\)({.*?})?\|
  return new RegExp(`${controlChar}(?!\\\\)({.*?})?${controlChar}`, "g");
}

// ################################ RENDERING COMPONENT ################################

const _Text = (propsxd: TextProps) => {
  let props = { ...propsxd };

  props._align = props._align ?? "left";
  props._type = props._type ?? "body";
  props._variant = props._variant ?? 1;
  props._del_margin = props._del_margin ?? false;
  props.className += " " + props._type + "-" + props._variant + " ";

  // Defaults
  const table: {
    /** Ej. "*" "#" "=" */
    [x: string]: {
      /** Ej. "_bold"  */
      _className: string;
      /** Ej. { _col: string }  */
      _props?: {
        [x: string]: string;
      };
    };
  } = {
    [props._styleText?._bold?._wrap || "*"]: {
      _className: "_bold",
    },
    [props._styleText?._italics?._wrap || "_"]: {
      _className: "_italics",
    },
    [props._styleText?._cross?._wrap || "~"]: {
      _className: "_cross",
    },
    [props._styleText?._mute?._wrap || "#"]: {
      _className: "_mute",
    },
    [props._styleText?._underline?._wrap || "&"]: {
      _className: "_underline",
    },
    [props._styleText?._emphasis?._wrap || "$"]: {
      _className: "_emphasis",
      _props: {
        _col: "#1757cd",
      },
    },
    [props._styleText?._link?._wrap || "="]: {
      _className: "_link",
      _props: {
        _to: "/",
        _as: "1",
      },
    },
    [props._styleText?._fontSize?._wrap || "^"]: {
      _className: "_fontSize",
      _props: {
        _ln: "16px",
      },
    },
    [props._styleText?._custom?._wrap || "@"]: {
      _className: "_custom",
      _props: {
        _add: "",
      },
    },
    [props._styleText?._converter?._wrap || "?"]: {
      _className: "_converter",
      _props: {
        _el: "span",
        _prop: "$0",
      },
    },
  };

  const _formatText = {
    _enable: props._styleText?._formatText?._enable ?? true,
    _newLine: props._styleText?._formatText?._newLine ?? ";;",
    _translate: props._styleText?._formatText?._translate ?? true,
  };

  // ############################ STYLE TEXT V2 ###########################
  // ############################ FORMAT TEXT
  if (_formatText._enable) {
    props._text = formatText(
      props._text,
      _formatText._newLine,
      _formatText._translate
    );
  }

  // ############################ VARIABLES & CONSTANTS
  /** Ej. { className: "_bold _emphasis _link", _col: string, _to: string } */
  type elProps = {
    className: string;
    [x: string]:
      | string
      | { _style?: _CSSProperties; _className?: string }
      | _HTMLProps;
  };

  type processType = {
    open: boolean;
    start: number;
    end: number;
    /** Ej. { className: "_bold _emphasis _link", _col: string, _to: string } */
    properties: elProps;
  };

  // Manejar el orden de los elementos
  const process = new Array<processType>();

  // Iniciar las expresiones regulares
  const regEx = styleTextSearch(props._styleText?._controlChar || undefined);
  const regExProps = CONSTANTS._REGEXPS._getProperties;

  // Crear una estructura para manejar los elementos creados
  const pila = new Array<{
    elementChildren: Array<React.ReactNode>;
    /** Ej. { className: "_bold _emphasis _link", _col: string, _to: string } */
    elementProps: elProps;
    elementKey: number;
  }>();

  // Dónde almacenar todos los elementos
  const htmlChildren = new Array<React.ReactNode>();

  // ############################ SEARCH EVERY CONTROL CHARACTER

  const emergencyBreak = Date.now();

  // Comienza buscando todos los elementos
  while (true && Date.now() - emergencyBreak <= 1000) {
    const match = regEx.exec(props._text);
    if (match === null) break;

    // Crear una variable para almacenar las propiedades del elemento global
    var properties: elProps = {
      className: "",
    };

    // Si el elemento abre, busca todos los estilos del elemento
    if (match[1]) {
      // Extraemos los estilos proporcionados
      const temp = match[1].slice(1, -1);

      // Buscar todos los estilos
      while (true && Date.now() - emergencyBreak <= 1000) {
        const propMatch = regExProps.exec(temp);
        if (propMatch === null) break;

        /** Nombre del estilo según su wrap/alias (ej. "*") */
        const propName = propMatch[1].trim();

        /** Varios props pasados para el estilo */
        const propProps = propMatch[2]?.trim();

        /** Valor sencillo interpretado como string, puede ser $2 para referirse a la posicion 2 de un arreglo */
        const propValue = propMatch[3]?.trim();

        /** Valores defaults del estilo */
        const defaults = table[propName];

        // Shortcut
        const className = table[propName]._className;

        // Agregamos la clase correspondiente a las propiedades del elemento global
        properties.className += className + " ";

        // Comenzamos a crear el estilo
        var estilos: { [x: string]: string } = {};

        // Si al estilo le pasamos props, buscarlos
        if (propProps) {
          const regex2 = CONSTANTS._REGEXPS._getProperties;

          // Comenzamos a buscar todos los props del estilo
          while (true && Date.now() - emergencyBreak <= 1000) {
            const subpropmatch = regex2.exec(propProps);
            if (subpropmatch === null) break;

            // Agregamos un "_" en caso de necesitarlo
            var a = subpropmatch[1].trim();
            if (a[0] !== "_") a = "_" + a;

            // Agregamos simplemente
            estilos = {
              ...estilos,
              [a]: subpropmatch[2]?.trim() || subpropmatch[3]?.trim(),
            };
          }
        } // Se han resuelto todos los props del estilo

        // Iteramos sobre las posibles propiedades del estilo (si existen)
        for (const key in defaults._props) {
          // Guardamos temporalmente el valor para la propiedad del estilo
          /** Ej. "#123123" */
          var aux:
            | string
            | { _style?: _CSSProperties; _className?: string }
            | _HTMLProps
            | undefined = propValue || estilos?.[key]; // Le ponemos el valor simple (si existe) o extraido del mapa (si existe)

          // En caso de que el valor simple contenga un indice de array, buscarlo
          if (propValue?.[0] === "$") {
            var arr: Array<any> | undefined =
              props._styleText?.[className]?.[key];
            aux =
              arr?.[
                boundaries(
                  parseNumber(propValue.slice(1).trim()),
                  0,
                  arr?.length
                )
              ];
            // En este punto, aux tiene el valor del indice del array
          }

          // Si aux no ha logrado nada, se usa el default
          if (!aux) aux = defaults._props[key];

          properties = {
            ...properties,
            [key]: aux,
          };
        }
      }
    } // Se han resuelto todos los estilos del elemento

    process.push({
      open: match[1] ? true : false,
      start: match.index,
      end: match.index + match[0].length,
      properties: match[1] ? properties : { className: "" },
    });
  } // Se han resuelto todos los elementos

  // ############################ PROCESSING VARIABLES

  var newText = props._text.slice(0, process[0]?.start); // Obtener el texto inicial previo a cualquier estilo

  htmlChildren.push(escapeChars(newText)); // Agregar el texto inicial

  // ################################################### Si un elemento previo esta por cerrar
  function popElement() {
    // Flotamos el último elemento, para que no se pierda
    var floatEl = pila.pop();
    if (floatEl === undefined) return;

    var collapsed: {
      _col?: string;
      _to?: string;
      _as?: string;
      _ln?: string;
      _add?: string | _CSSProperties;
      _el?: string;
      _prop?: _HTMLProps;
      className?: string;
    } = {};

    // Interpretamos las propiedades personalizadas
    for (const key in floatEl.elementProps) {
      collapsed[key] = floatEl.elementProps[key];
    }

    const tempStyle: _HTMLProps & { key: number | string } = {
      className: (
        collapsed.className +
        " " +
        (collapsed._el ? "as-" + collapsed._el + " " : "") +
        (collapsed._as ? "_link-" + collapsed._as + " " : "") +
        (typeof collapsed._add === "string" ? collapsed._add : "")
      ).trim(),
      style: {
        color: collapsed._col,
        fontSize: collapsed._ln,
        ...(typeof collapsed._add !== "string" ? collapsed._add : {}),
      },
      ...collapsed._prop,
      key: floatEl.elementKey,
    };

    var el;

    try {
      el = React.createElement(
        collapsed._el || "span",
        tempStyle,
        floatEl.elementChildren
      );
    } catch (error) {
      console.log(error);
      el = React.createElement("span", tempStyle, floatEl.elementChildren);
    }

    // Creamos el elemento para agregarlo
    const newEl = collapsed.className?.includes("_link") ? (
      <Link
        to={collapsed._to || "/"}
        {...asType<React.AnchorHTMLAttributes<HTMLAnchorElement>>(tempStyle)}
      >
        {floatEl.elementChildren}
      </Link>
    ) : (
      el
    );

    // Al elemento flotado no se le agrega nada, simplemente se mete dentro del siguiente elemento en la pila, o el html principal
    if (pila.length) pila[pila.length - 1].elementChildren.push(newEl);
    else htmlChildren.push(newEl);
  }

  // ############################ PROCESSING CYCLES
  process.forEach((value, index) => {
    /** Texto plano que lleva como base */
    const currentText = props._text.slice(value.end, process[index + 1]?.start);

    // ######################################### Si un nuevo elemento esta comenzando
    if (value.open) {
      // Lo insertamos a la pila de elementos
      pila.push({
        elementChildren: [escapeChars(currentText)], // Le agregamos el texto base
        elementProps: value.properties,
        elementKey: index,
      });
    } else {
      popElement();

      // Una vez que se hayan cerrado todos los que estuvieran abiertos, agregamos el texto residual al ultimo elemento, o el html
      if (pila.length)
        pila[pila.length - 1].elementChildren.push(escapeChars(currentText));
      else htmlChildren.push(escapeChars(currentText));
    }
  });

  // Crear el resto de elementos de la pila si no se cerraron normalmente
  while (pila.length) popElement();

  // Interpretamos los props para el elemento principal

  var collapsed: {
    _col?: string;
    _to?: string;
    _as?: string;
    _ln?: string;
    _add?: string | _CSSProperties;
    _el?: string;
    _prop?: _HTMLProps;
    className: string;
  } = {
    className: "text " + props.className + " ",
  };

  // Interpretamos las propiedades personalizadas
  for (const key in props._this) {
    collapsed.className += key + " ";
    for (const subkey in props._this[key]) {
      const val = props._this[key][subkey];

      if (typeof val === "string" && val[0] === "$") {
        var c: Array<any> | undefined = props._styleText?.[key]?.[subkey];

        collapsed[subkey] =
          c?.[boundaries(parseNumber(val.slice(1).trim()), 0, c?.length)];
      } else collapsed[subkey] = val;
    }
  }

  const tempStyle: _HTMLProps = {
    className: (
      collapsed.className +
      " " +
      (collapsed._el ? "as-" + collapsed._el + " " : "") +
      (collapsed._as ? "_link-" + collapsed._as + " " : "") +
      (typeof collapsed._add === "string" ? collapsed._add : "")
    ).trim(),
    style: {
      color: collapsed._col,
      fontSize: collapsed._ln,
      ...(typeof collapsed._add !== "string" ? collapsed._add : {}),
    },
    ...collapsed._prop,
  };

  var el;

  try {
    el = React.createElement(collapsed._el || "div", tempStyle, htmlChildren);
  } catch (error) {
    console.log(error);
    el = React.createElement("div", tempStyle, htmlChildren);
  }

  // Creamos el elemento para agregarlo
  const textElement = props._this?._link ? (
    <Link
      to={collapsed._to || "/"}
      {...asType<React.AnchorHTMLAttributes<HTMLAnchorElement>>(tempStyle)}
    >
      {htmlChildren}
    </Link>
  ) : (
    el
  );

  // Crear un elemento contenedor y regresarlo
  return textElement;
  // ############################ END OF STYLE TEXT V2 ###########################
};

// ################################ STYLED ################################

const Text = styled(_Text).attrs((props): TextProps => {
  return {
    _align: "left",
    _type: "body",
    _del_margin: false,
    ...props,
  };
})`
  ${(props) => css`
    text-align: ${props._align};
    margin: ${props._del_margin ? "0px" : ""};

    color: ${props._colors?._text};
    border-color: ${props._colors?._border};
    background-color: ${props._colors?._back};

    &._bold,
    ._bold {
      font-weight: 700;
    }

    &._italics,
    ._italics {
      font-style: italic;
    }

    &._cross,
    ._cross {
      text-decoration: line-through;
    }

    &._mute,
    ._mute {
      opacity: 50%;
    }

    &._underline,
    ._underline {
      text-decoration: underline;
    }

    &._emphasis,
    ._emphasis {
      color: ${props._colors?._emphasis};
    }

    &._link,
    ._link {
      cursor: pointer;
    }

    &::before,
    &::after {
      color: ${props._colors?._text};
      border-bottom-color: ${props._colors?._emphasis};
    }

    &:visited {
      color: ${props._colors?._visited};
      &::before,
      &::after {
        border-bottom-color: ${props._colors?._emphasis};
      }
    }

    &:hover {
      color: ${props._colors?._hover};
      &::before,
      &::after {
        border-bottom-color: ${props._colors?._emphasis};
      }
    }

    &:active {
      color: ${props._colors?._active};
      &::before,
      &::after {
        border-bottom-color: ${props._colors?._emphasis};
      }
    }

    ${parseCSS(props._style)}
  `}
`;

// ################################ EXPORTS ################################

export default Text;
